import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import { ImageUploadService } from './imageUploadService';
import {
  ImageLibraryFolderService,
  ImageLibraryImageService,
  ImageLibraryFolderDto,
} from './imageLibraryService';

/**
 * Toplu Ürün Resmi Yükleme (Barkod Klasörü)
 *
 * Tacreo mantığı: klasör adı = barkod, içindeki dosyalar barkod-1, barkod-2...
 * Klasörler tarayıcıya yüklenir, barkoddan ürün bulunur ve resimler ürünün
 * mevcut galerisini KORUYARAK eklenir.
 */

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'];

// ====== TYPES ======

export interface ParsedFile {
  file: File;
  /** Dosya adındaki sıra numarası (barkod-1 -> 1). Yoksa null. */
  order: number | null;
  /** Uzantısız dosya adı */
  baseName: string;
}

export interface ParsedFolder {
  /** Klasör adı = barkod */
  barcode: string;
  files: ParsedFile[];
}

export interface BarcodeResolveResult {
  barcode: string;
  found: boolean;
  itemId?: string | null;
  name?: string | null;
  stockCode?: string | null;
  imageUrl?: string | null;
  imageUrls: string[];
  duplicateCount: number;
  categoryId?: string | null;
  categoryName?: string | null;
}

export interface BulkAppendImageResultItem {
  itemId: string;
  success: boolean;
  addedCount: number;
  skippedCount: number;
  imageUrls: string[];
  error?: string | null;
}

export interface BulkAppendImagesResult {
  totalCount: number;
  successCount: number;
  skippedCount: number;
  failedCount: number;
  results: BulkAppendImageResultItem[];
}

export type AssignProgressPhase = 'uploading' | 'library' | 'assigning' | 'done';

export interface AssignProgress {
  phase: AssignProgressPhase;
  /** İşlenen klasör/ürün sayısı */
  current: number;
  /** Toplam klasör/ürün sayısı */
  total: number;
  /** Şu an işlenen barkod */
  currentBarcode?: string;
}

export interface AssignFolderOutcome {
  barcode: string;
  itemId: string;
  uploadedCount: number;
  addedCount: number;
  skippedCount: number;
  success: boolean;
  error?: string;
}

export interface AssignRunResult {
  outcomes: AssignFolderOutcome[];
  successFolders: number;
  failedFolders: number;
  totalAdded: number;
}

// ====== PARSE ======

function getExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : '';
}

function stripExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(0, idx) : fileName;
}

function isImageFile(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  return IMAGE_EXTENSIONS.includes(getExtension(file.name));
}

/** Dosya adının sonundaki "-N" veya "_N" sıra numarasını çıkarır. */
function parseOrder(baseName: string): number | null {
  const match = baseName.match(/[-_](\d+)\s*$/);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * webkitdirectory ile seçilen dosyaları barkod klasörlerine gruplar.
 * Barkod = dosyayı doğrudan içeren klasör adı (relativePath'in sondan bir önceki segmenti).
 * Üst klasör seçilince (parent/barkod1/a.jpg, parent/barkod2/b.jpg) tüm barkodlar gelir.
 * Windows yollarındaki \ de desteklenir.
 */
export function parseFolderSelection(fileList: FileList | File[]): ParsedFolder[] {
  const files = Array.from(fileList);
  const groups = new Map<string, ParsedFile[]>();

  for (const file of files) {
    if (!isImageFile(file)) continue;

    // webkitRelativePath örn: "8690.../8690...-1.jpg" veya "parent/8690.../img-1.jpg"
    const relativePath: string =
      (file as any).webkitRelativePath || (file as any).relativePath || file.name || '';
    const segments = relativePath.replace(/\\/g, '/').split('/').filter(Boolean);

    // Dosyayı doğrudan içeren klasör = sondan bir önceki segment
    let barcode: string | null = null;
    if (segments.length >= 2) {
      barcode = segments[segments.length - 2].trim();
    }
    if (!barcode) continue; // kök seviyesindeki (klasörsüz) dosyaları atla

    const baseName = stripExtension(file.name);
    const parsed: ParsedFile = {
      file,
      baseName,
      order: parseOrder(baseName),
    };

    const key = barcode; // görünen ad olarak orijinal klasör adını koru
    const existing = groups.get(key);
    if (existing) existing.push(parsed);
    else groups.set(key, [parsed]);
  }

  const result: ParsedFolder[] = [];
  for (const [barcode, groupFiles] of groups) {
    groupFiles.sort((a, b) => {
      if (a.order != null && b.order != null) return a.order - b.order;
      if (a.order != null) return -1;
      if (b.order != null) return 1;
      return a.baseName.localeCompare(b.baseName, 'tr');
    });
    result.push({ barcode, files: groupFiles });
  }

  result.sort((a, b) => a.barcode.localeCompare(b.barcode, 'tr'));
  return result;
}

// ====== API ======

export async function resolveByBarcodes(
  companyId: string,
  barcodes: string[]
): Promise<BarcodeResolveResult[]> {
  const response = await accountingApi.post<BarcodeResolveResult[]>(
    ACCOUNTING_API.INVENTORY_ITEM_RESOLVE_BY_BARCODES,
    { companyId, barcodes }
  );
  return response.data;
}

export async function bulkAppendImages(
  companyId: string,
  items: { itemId: string; urls: string[] }[],
  maxImagesPerProduct = 8
): Promise<BulkAppendImagesResult> {
  const response = await accountingApi.post<BulkAppendImagesResult>(
    ACCOUNTING_API.INVENTORY_ITEM_BULK_APPEND_IMAGES,
    { companyId, items, maxImagesPerProduct }
  );
  return response.data;
}

// ====== ORCHESTRATION ======

async function refreshFolderCache(
  companyId: string,
  folderCache: Map<string, string>
): Promise<void> {
  const existing = await ImageLibraryFolderService.getAllFolders(companyId);
  folderCache.clear();
  for (const f of existing) {
    if (f?.id && f?.name) folderCache.set(f.name.toLowerCase(), f.id);
  }
}

/**
 * Barkod adlı kütüphane klasörünü garanti eder.
 * Oluşturma başarısız olursa (isim çakışması vb.) listeyi yenileyip mevcut klasörü bulur.
 * Artık sessizce "klasörsüz / ana dizin"e düşmez.
 */
async function ensureFolder(
  companyId: string,
  name: string,
  folderCache: Map<string, string>,
  createdBy?: string
): Promise<string> {
  const key = name.toLowerCase().trim();
  const cached = folderCache.get(key);
  if (cached) return cached;

  try {
    const folder = await ImageLibraryFolderService.createFolder({
      companyId,
      name: name.trim(),
      createdBy,
    });
    if (folder?.id) {
      folderCache.set(key, folder.id);
      return folder.id;
    }
  } catch {
    // create başarısız → mevcut klasörü ara
  }

  try {
    await refreshFolderCache(companyId, folderCache);
    const found = folderCache.get(key);
    if (found) return found;
  } catch {
    // aşağıda throw
  }

  throw new Error(`Kütüphane klasörü oluşturulamadı / bulunamadı: ${name}`);
}

export interface RunBulkAssignParams {
  companyId: string;
  createdBy?: string;
  /** Sadece eşleşmiş (found) klasörler gönderilmeli */
  folders: ParsedFolder[];
  /** barcode -> itemId eşlemesi (resolve sonucundan) */
  barcodeToItemId: Map<string, string>;
  maxImagesPerProduct?: number;
  onProgress?: (progress: AssignProgress) => void;
}

/**
 * Seçilen klasörleri Image Service'e yükler, barkod adlı kütüphane klasörüne
 * kaydeder ve ürünlere (mevcut galeriyi koruyarak) ekler.
 */
export async function runBulkAssign(params: RunBulkAssignParams): Promise<AssignRunResult> {
  const {
    companyId,
    createdBy,
    folders,
    barcodeToItemId,
    maxImagesPerProduct = 8,
    onProgress,
  } = params;

  const outcomes: AssignFolderOutcome[] = [];
  const total = folders.length;

  // Mevcut kütüphane klasörlerini önbelleğe al
  const folderCache = new Map<string, string>();
  try {
    const existingFolders: ImageLibraryFolderDto[] =
      await ImageLibraryFolderService.getAllFolders(companyId);
    for (const f of existingFolders) {
      folderCache.set(f.name.toLowerCase(), f.id);
    }
  } catch {
    // klasör listesi alınamazsa yeni oluşturmaya çalışılır
  }

  // Ürüne eklenecek URL'ler (assign adımında toplu gönderilecek)
  const appendItems: { itemId: string; urls: string[] }[] = [];
  const outcomeByItemId = new Map<string, AssignFolderOutcome>();

  let processed = 0;
  for (const folder of folders) {
    const itemId = barcodeToItemId.get(folder.barcode);
    processed++;

    if (!itemId) continue; // eşleşmeyenler zaten filtrelenmiş olmalı

    const outcome: AssignFolderOutcome = {
      barcode: folder.barcode,
      itemId,
      uploadedCount: 0,
      addedCount: 0,
      skippedCount: 0,
      success: false,
    };

    try {
      onProgress?.({
        phase: 'uploading',
        current: processed,
        total,
        currentBarcode: folder.barcode,
      });

      // 1) Image Service'e yükle (klasördeki tüm dosyalar, sırayla)
      const filesToUpload = folder.files.map((f) => f.file);
      const uploaded = await ImageUploadService.uploadImages(filesToUpload);
      outcome.uploadedCount = uploaded.length;

      // 2) Kütüphaneye barkod adlı klasöre kaydet (klasörsüz ana dizine düşmez)
      onProgress?.({
        phase: 'library',
        current: processed,
        total,
        currentBarcode: folder.barcode,
      });
      let folderId: string | undefined;
      try {
        folderId = await ensureFolder(companyId, folder.barcode, folderCache, createdBy);
      } catch (folderErr: any) {
        // Ürüne atama devam eder; klasör hatası sonuçta görünür
        outcome.error = folderErr?.message || 'Kütüphane klasörü oluşturulamadı';
      }

      if (folderId) {
        for (let i = 0; i < uploaded.length; i++) {
          const up = uploaded[i];
          const src = folder.files[i];
          try {
            await ImageLibraryImageService.createImage({
              companyId,
              folderId,
              name: src ? src.baseName : `${folder.barcode}-${i + 1}`,
              url: up.originalUrl,
              thumbnailUrl: up.thumbnailUrl,
              type: src?.file.type,
              size: src?.file.size,
              createdBy,
            });
          } catch {
            // Kütüphane kaydı başarısız olsa da ürüne atama devam etsin
          }
        }
      }

      // 3) Ürüne atama için URL'leri biriktir (sırayı koru)
      outcome.success = true;
      const urls = uploaded.map((u) => u.originalUrl).filter(Boolean);
      appendItems.push({ itemId, urls });
      outcomeByItemId.set(itemId, outcome);
    } catch (err: any) {
      outcome.error = err?.message || 'Yükleme hatası';
      outcome.success = false;
    }

    outcomes.push(outcome);
  }

  // 4) Ürünlere toplu ekle (mevcut galeriyi koruyarak)
  if (appendItems.length > 0) {
    onProgress?.({ phase: 'assigning', current: total, total });
    try {
      const assignResult = await bulkAppendImages(companyId, appendItems, maxImagesPerProduct);
      for (const r of assignResult.results) {
        const oc = outcomeByItemId.get(r.itemId);
        if (oc) {
          oc.addedCount = r.addedCount;
          oc.skippedCount = r.skippedCount;
          if (!r.success) {
            oc.success = false;
            oc.error = r.error || 'Atama hatası';
          }
        }
      }
    } catch (err: any) {
      // Atama toptan başarısız olursa yüklenen klasörleri hatalı işaretle
      for (const item of appendItems) {
        const oc = outcomeByItemId.get(item.itemId);
        if (oc) {
          oc.success = false;
          oc.error = err?.message || 'Toplu atama hatası';
        }
      }
    }
  }

  onProgress?.({ phase: 'done', current: total, total });

  const successFolders = outcomes.filter((o) => o.success).length;
  const failedFolders = outcomes.filter((o) => !o.success).length;
  const totalAdded = outcomes.reduce((sum, o) => sum + o.addedCount, 0);

  return { outcomes, successFolders, failedFolders, totalAdded };
}
